<template>
  <FeedSelectStyled>
    <Multiselect
      class="custom_multiselect"
      track-by="id"
      :label="label"
      :value="value"
      :disabled="disabled"
      :placeholder="placeholder"
      :options="options"
      :close-on-select="closeOnSelect"
      :max-height="maxHeight"
      :show-no-results="showNoResults"
      :hide-selected="hideSelected"
      :show-labels="false"
      :searchable="searchable"
      :resetAfter="resetAfter"
      :multiple="multiple"
      :internal-search="internalSearch"
      :loading="isLoading"
      :limit-text="limitText"
      :open-direction="openDirection"
      :clear-on-select="clearOnSelect"
      :allow-empty="allowEmpty"
      @search-change="$emit('search-change', $event)"
      @select="$emit('select', $event)"
      @remove="$emit('remove', $event)"
    >
      <template slot="selection" slot-scope="{ isOpen }"
        ><span v-if="value && value.length === 0 && isOpen">{{ placeholder }}</span></template
      >
      <template slot="option" slot-scope="{ option }" class="icon">
        <TalpaIcon v-if="categoryIcon" class="icon" :scope="'Feed'" :name="`${option.label}`" />
        {{ customOptions ? $t(customOptions + option.label) : option.label }} <CheckIcon />
      </template>
      <template slot="singleLabel" slot-scope="{ option }">
        <slot name="customLabelIcon" class="labelIcon" />
        <TalpaIcon v-if="categoryIcon" class="icon" :scope="'Feed'" :name="`${option.label}`" />
        <span class="labelText"> {{ customLabels ? $t(customLabels + option.label) : option.label }} </span>
      </template>
      <MoreHorizontalIcon
        slot-scope="{ toggle }"
        slot="caret"
        class="multiselect__select caretIcon"
        @mousedown.prevent.stop="toggle"
        v-if="customCaretIcon"
      />
      <ChevronDownIcon slot-scope="{ toggle }" slot="caret" class="multiselect__select caretIcon" @mousedown.prevent.stop="toggle" v-else />
      <template v-if="customTagSlot" slot="tag" slot-scope="{ option }">
        <slot name="tag" :option="option" />
      </template>
      <template v-if="customNoOptionsSlot" slot="noOptions">
        <slot name="noOptions" />
      </template>
    </Multiselect>
  </FeedSelectStyled>
</template>

<script>
import { styled } from '@egoist/vue-emotion'
import { ChevronDownIcon, CheckIcon, MoreHorizontalIcon } from 'vue-feather-icons'
import Multiselect from 'vue-multiselect'
import TalpaIcon from '@common/components/src/TalpaIcon'

const FeedSelectStyled = styled('div')`
  display: flex;
  align-items: center;
  .custom_multiselect {
    margin-top: 0.1rem;
    width: 100%;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    border: none;
    &:hover {
      border: none;
    }
    & .multiselect__option--selected {
      background: ${p => p.theme.colors.primary} !important;
      color: white !important;
      .icon {
        width: 20px;
        height: 20px;
        display: inline-block !important;
      }
    }
    .multiselect__option--highlight {
      background: ${p => p.theme.colors.atomic.hover};
      color: ${p => (p.theme.isDark ? p.theme.colors.white : p.theme.colors.black)};
    }
    .multiselect__option {
      > svg {
        display: inline-block !important;
        float: right;
        width: 15px;
        height: 15px;
        opacity: 0;
        transition: opacity 0.5s;
      }
      &.multiselect__option--selected {
        > svg {
          opacity: 1;
        }
      }
      .icon {
        width: 20px;
        height: 20px;
        display: inline-block !important;
      }
    }
    .multiselect__content-wrapper {
      top: 3rem;
      border-radius: 5px;
      border: none;
    }
    .multiselect__single {
      font-size: 14px;
      top: 0.1rem;
      background: transparent;
      color: ${p => p.theme.colors.primary};
      display: flex;
      flex-direction: row;
      grid-gap: 0.75rem;
      > svg {
        vertical-align: middle;
      }
      .icon {
        width: 20px;
        height: 20px;
      }
    }
    .multiselect__tags {
      background: transparent;
    }
    .caretIcon {
      stroke: ${({ theme }) => theme.colors.primaryDarker};
    }
    .multiselect__input {
      background: transparent;
    }
  }
`
export default {
  props: {
    options: {
      type: Array,
      required: true,
    },
    customOptions: {
      type: String,
      required: false,
    },
    customLabels: {
      type: String,
      required: false,
    },
    closeOnSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    customCaretIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    categoryIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      required: false,
    },
    shouldNotResetSelectedOption: {
      type: Boolean,
      required: false,
    },
    showNoResults: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchable: {
      type: Boolean,
      required: false,
      default: false,
    },
    resetAfter: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    internalSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
    },
    modelName: {
      type: String,
      required: false,
      default: 'Unknown',
    },
    openDirection: {
      type: String,
    },
    maxHeight: {
      type: Number,
      default: 500,
    },
    applyFilter: {
      type: Boolean,
      default: true,
    },
    clearOnSelect: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'label',
    },
    customTagSlot: {
      type: Boolean,
      default: false,
    },
    customNoOptionsSlot: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    FeedSelectStyled,
    Multiselect,
    ChevronDownIcon,
    CheckIcon,
    MoreHorizontalIcon,
    TalpaIcon,
  },
  watch: {
    selectedOption: {
      handler() {
        this.selectedValue()
      },
      immediate: true,
    },
  },
  data() {
    return {
      filter: {},
    }
  },
  created() {
    this.$root.$on('reset', this.resetFilter) // resets the filter & loads the first option by default
    if (!this.shouldNotResetSelectedOption) {
      this.resetFilter() // preselected option
    }
  },
  computed: {},
  methods: {
    limitText(count) {
      return `and ${count} other ${this.$tc(this.modelName, count)}`
    },
    selectedFilter(option) {
      this.filter = []
      this.filter = option
      this.$emit('selectedFilter', this.filter)
    },
    resetFilter() {
      this.filter = this.options[0]
    },
    selectedValue() {
      if (this.applyFilter) {
        this.filter = this.selectedOption
      }
    },
  },
}
</script>
